import { FabricImage, InteractiveFabricObject } from 'fabric'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Form } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import {
  BiAlignLeft,
  BiAlignMiddle,
  BiAlignRight,
  BiBold,
  BiCircle,
  BiCrop,
  BiItalic,
  BiMinus,
  BiRectangle,
  BiRedo,
  BiText,
  BiTrash,
  BiUnderline,
  BiUndo,
} from 'react-icons/bi'
import {
  MdArrowBack,
  MdAssignmentTurnedIn,
  MdDelete,
  MdFlipToBack,
  MdFlipToFront,
  MdOpenWith,
  MdOutlineChangeHistory,
  MdPhotoSizeSelectLarge,
  MdPreview,
  MdRotateLeft,
  MdSave,
  MdSaveAs,
  MdZoomIn,
  MdZoomOut,
} from 'react-icons/md'
import { ToastContainer, toast } from 'react-toastify'

import {
  Accordion,
  AccordionItem,
  AccordionItemButton,
  AccordionItemHeading,
  AccordionItemPanel,
} from 'react-accessible-accordion'
import { AiOutlineLine } from 'react-icons/ai'
import axiosClient, {
  updateBook,
  uploadPdfToBucket,
} from 'services/axios-client'
import { BookCoverContainer } from './style'

import { SyncOutlined } from '@ant-design/icons'
import { Input, Radio } from 'antd'
import AWS from 'aws-sdk'
import classNames from 'classnames'
import { ColorPicker } from 'components/ColorPicker'
import StyledLoader from 'components/Elements/StyledLoader'
import { isValidImageBlob, remoteImageToFile } from 'lib/image'
import queryString from 'query-string'
import {
  useHistory,
  useLocation,
  useParams,
  withRouter,
} from 'react-router-dom'
import { useBookQuery, useBookStatsQuery } from 'store/queries'
import { colors } from 'style'
import BackgroundSelector from './components/BackgroundSelector'
import DeleteCoverModal from './components/DeleteCoverModal'
import SaveCoverModal from './components/SaveCoverModal'
import { PIXELS_PER_MM } from './constants'
import useCanvasHistory from './hooks/useCanvasHistory'
import useCrop from './hooks/useCrop'
import useFabricCanvases from './hooks/useFabricCanvases'
import usePan from './hooks/usePan'
import { useCoverEditorTour } from './hooks/useTour'
import {
  createPdf,
  fonts,
  generateThumbnail,
  initializeFrontOrBackCover,
  initializeSpineBackground,
  loadBackgroundImage,
  loadFonts,
  uploadImageToBucket,
  uploadThumbnailToBucket,
  zoomCanvas,
} from './util/helpers'
import {
  useCanvasObjectCreators,
  useCanvasObjectTools,
  useCanvasTextTools,
} from './util/tools'
// Override control defaults to make them more visible.
// TODO: Move fabric stuff into a lib file?
InteractiveFabricObject.ownDefaults = {
  ...InteractiveFabricObject.ownDefaults,
  cornerSize: 40,
  cornerStrokeColor: colors.blueDark,
  cornerColor: '#00008216',
  transparentCorners: false,
  cornerDashArray: [2, 2],
  borderColor: 'orange',
  borderDashArray: [3, 1, 3],
  borderScaleFactor: 2,
}

// aws config
AWS.config.update({
  accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
  secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
  region: process.env.REACT_APP_AWS_REGION,
})

// Prevent scaling images to a size where printing would suffer.
function handleScaleImg(e, canvas, img) {
  if (img.scaleX > 1 || img.scaleY > 1) {
    img.scaleX = Math.min(img.scaleX, 1)
    img.scaleY = Math.min(img.scaleY, 1)

    // Reset position, otherwise the image will jump around if resizing up/leftwards
    img.left = e.transform.original.left
    img.top = e.transform.original.top

    img.setCoords()
    canvas.requestRenderAll()

    toast.error(
      "Increasing this image above it's current size will make it not detailed enough for printing. Please add a higher resolution image to scale it further.",
      { toastId: 'img-warning' },
    )
  }
}

function CoverEditor() {
  const location = useLocation()
  const routerHistory = useHistory()
  const showTour = useCoverEditorTour()

  const { bookId, coverId } = useParams()
  const { isNew, templateId } = queryString.parse(location.search)

  const { t } = useTranslation()

  const { data: bookDetail } = useBookQuery(bookId)
  const { data: stats } = useBookStatsQuery(bookId)

  const frontCanvasElRef = useRef()
  const backCanvasElRef = useRef()
  const spineCanvasElRef = useRef()

  const canvasWrapperElRef = useRef()

  const [activeCanvas, setActiveCanvas] = useState(null)
  const [strokeColorValue, setStrokeColorValue] = useState('black')
  const [fillColorValue, setFillColorValue] = useState('#fff0')
  const [fontFamilyValue, setfontFamilyValue] = useState('')
  const [remoteImgURL, setRemoteImgURL] = useState('')
  const [coverName, setCoverName] = useState('')
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showSaveCoverModal, setShowSaveCoverModal] = useState(false)
  const [currentEditTarget, setCurrentEditTarget] = useState('front')
  const [backgroundColor, setBackgroundColor] = useState('#ffffff')
  const [strokeWidth, setStrokeWidth] = useState(1)
  const [addingRemoteImg, setAddingRemoteImg] = useState(false)
  const [addingLocalImg, setAddingLocalImg] = useState(false)

  const [canvasSpacerConfig, setCanvasSpacerConfig] = useState({
    isPillarBoxed: false,
    width: '100%',
    height: '100%',
  })

  const isTourShown = localStorage.getItem('is-cover-tour-shown')

  useEffect(() => {
    if (isNew) {
      // If the user specifically creates a new canvas, start from scratch.
      localStorage.removeItem('cover-history')
      routerHistory.replace({
        pathname: location.pathname,
        search: '',
      })
    }
  }, [isNew, location.pathname, routerHistory])

  const {
    undo,
    redo,
    canUndo,
    canRedo,
    spineHistory,
    frontHistory,
    backHistory,
    canvasHistory,
    dispatchHistory,
    setDisableUndo,
    ignoreHistoryRef,
    pushHistory,
  } = useCanvasHistory(activeCanvas, currentEditTarget)

  const pageCount = stats?.estimated_page_count

  // See Lulu Book Creation Guide for formula:
  // https://assets.lulu.com/media/guides/en/lulu-book-creation-guide.pdf
  const spineWidth = PIXELS_PER_MM * (pageCount / 17.48 + 1.524)

  const {
    frontCanvas,
    backCanvas,
    spineCanvas,
    renderCanvases,
    frontBackgroundImage,
    spineBackgroundImage,
    backBackgroundImage,
    setBackBackgroundImage,
    setSpineBackgroundImage,
    setFrontBackgroundImage,
    activeSelection,
    setActiveSelection,
  } = useFabricCanvases(
    frontCanvasElRef,
    spineCanvasElRef,
    backCanvasElRef,
    spineWidth,
    pushHistory,
    dispatchHistory,
    bookId,
    coverId,
    templateId,
    pageCount,
    setStrokeWidth,
    setStrokeColorValue,
    setFillColorValue,
    setCoverName,
    setActiveCanvas,
  )

  const canvases = useMemo(
    () => ({
      front: frontCanvas,
      spine: spineCanvas,
      back: backCanvas,
    }),
    [frontCanvas, spineCanvas, backCanvas],
  )

  const activeObject = activeSelection?.length === 1 ? activeSelection[0] : null

  let currentCanvasBackgroundImg = frontBackgroundImage
  if (currentEditTarget === 'spine') {
    currentCanvasBackgroundImg = spineBackgroundImage
  } else if (currentEditTarget === 'back') {
    currentCanvasBackgroundImg = backBackgroundImage
  }

  const isActiveTextObject = activeObject?.type === 'textbox'
  const isActiveImgObject = activeObject?.type === 'image'

  // ------------------------------load json if it exists in session storage or show name modal.

  const canvasWrapperEl = canvasWrapperElRef.current

  const { isPanning, togglePanMode } = usePan(activeCanvas, canvases)
  const { cropImage, enterCropMode, applyCrop } = useCrop(
    activeCanvas,
    activeObject,
    ignoreHistoryRef,
    pushHistory,
    setDisableUndo,
  )
  const {
    addCircle,
    addLine,
    addRectangle,
    addText,
    addTextVerticallyTopToBottom,
    addTextVerticallyBottomToTop,
    addTriangle,
  } = useCanvasObjectCreators(activeCanvas, currentEditTarget)
  const {
    textFontFamily,
    textBold,
    textUnderline,
    textStrikeThrough,
    textItalic,
    textLeft,
    textCenter,
    textRight,
  } = useCanvasTextTools(
    activeCanvas,
    currentEditTarget,
    pushHistory,
    setfontFamilyValue,
  )
  const {
    sendForward,
    sendBackward,
    resetRotation,
    deleteObj,
    selectStrokeColor,
    selectFillColor,
    handleStrokeWidthChange,
  } = useCanvasObjectTools(
    activeCanvas,
    activeSelection,
    activeObject,
    pushHistory,
    setStrokeColorValue,
    setFillColorValue,
    setStrokeWidth,
    setActiveSelection,
  )

  useEffect(loadFonts, [])

  useEffect(
    function saveHistoryToLocalStorage() {
      if (canvasHistory?.key) {
        localStorage.setItem('cover-history', JSON.stringify(canvasHistory))
      }
    },
    [canvasHistory],
  )

  useEffect(() => {
    if (activeCanvas && !isTourShown) {
      showTour()
      localStorage.setItem('is-cover-tour-shown', 'true')
    }
  }, [activeCanvas, isTourShown, showTour])

  // We need to place buffers beside the cover in order to have the canvas maintain the correct aspect ratio,
  // whilst scaling to fit the available area. Fabricjs will have geometry errors if the canvas is scaled with css
  // while keeping the same element size. To remove this we would need to to decouple the size of the canvas from the cover size.
  useEffect(
    function initializeSpacers() {
      if (canvasWrapperEl && activeCanvas) {
        const setupCanvasSpacers = () => {
          const canvasWidth = activeCanvas.getWidth()
          const canvasHeight = activeCanvas.getHeight()

          const aspectRatio = canvasWidth / canvasHeight

          const canvasWrapperGeometry = canvasWrapperEl.getBoundingClientRect()
          const wrapperHeight = canvasWrapperGeometry.height
          const wrapperWidth = canvasWrapperGeometry.width
          const wrapperAspectRatio = wrapperWidth / wrapperHeight

          // Do we need to calc

          // Pillarboxing
          if (wrapperAspectRatio > aspectRatio) {
            const scalingFactor = wrapperHeight / canvasHeight
            const scaledWidth = canvasWidth * scalingFactor
            const pillarWidth = (wrapperWidth - scaledWidth) / 2

            setCanvasSpacerConfig({
              isPillarBoxed: true,
              width: pillarWidth,
              height: '100%',
            })
          } else {
            // Letterboxing
            const scalingFactor = wrapperWidth / canvasWidth
            const scaledHeight = canvasHeight * scalingFactor
            const bufferHeight = (wrapperHeight - scaledHeight) / 2

            setCanvasSpacerConfig({
              isPillarBoxed: false,
              width: '100%',
              height: bufferHeight,
            })
          }
        }

        setupCanvasSpacers()
        const observer = new ResizeObserver(setupCanvasSpacers)
        observer.observe(canvasWrapperEl)

        return () => {
          observer.disconnect()
        }
      }
    },
    [activeCanvas, canvasWrapperEl],
  )

  const handleWheel = (e) => {
    const coords = { x: e.clientX, y: e.clientY }

    if (e.deltaY < 0) {
      zoomCanvas(activeCanvas, 1, coords)
    } else {
      zoomCanvas(activeCanvas, -1, coords)
    }
  }

  const addRemoteImgByUrl = async (e, remoteUrl) => {
    e.preventDefault()
    setAddingRemoteImg(true)

    try {
      const file = await remoteImageToFile(remoteUrl)
      const storedUrl = await uploadImageToBucket(file, bookDetail?.id)

      const img = await FabricImage.fromURL(URL.createObjectURL(file))
      img.setSrc(storedUrl, {
        crossOrigin: 'anonymous',
      })
      activeCanvas.add(img)

      img.on('modified', (e) => handleScaleImg(e, activeCanvas, img))
      activeCanvas.renderAll()
    } catch (error) {
      toast.error(error.message || 'Failed to add remote image')
    } finally {
      setAddingRemoteImg(false)
      setRemoteImgURL('')
    }
  }

  const onUploadImage = async (e) => {
    if (e.target.files.length) {
      setAddingLocalImg(true)
      try {
        const image = e.target.files[0]

        if (!(await isValidImageBlob(image))) {
          throw new Error('The selected file is not a valid image.')
        }

        const url = await uploadImageToBucket(image, bookDetail?.id)

        const img = await FabricImage.fromURL(URL.createObjectURL(image))

        img.setSrc(url, {
          crossOrigin: 'anonymous',
        })
        activeCanvas.add(img)
        img.on('modified', (e) => handleScaleImg(e, activeCanvas, img))
        activeCanvas.renderAll()
      } catch (error) {
        toast.error(error.message || 'Failed to upload image')
      } finally {
        setAddingLocalImg(false)
      }
    }
  }

  const deleteCover = () => {
    if (coverId) {
      const deleteData = async () => {
        const templateResult = await axiosClient.get(
          `/templates/delete/${coverId}`,
        )

        if (templateResult.ok) {
          toast.success('Your Template is deleted!')

          routerHistory.replace(
            `/dashboard/book/${bookDetail.id}/publish/cover-templates`,
          )
        } else {
          toast.error('Failed to delete template')

          return
        }
      }
      deleteData()

      setCoverName('')
    }

    localStorage.removeItem('cover-history')

    Object.values(canvases).forEach((canvas) => {
      canvas.clear()
      canvas.setViewportTransform([1, 0, 0, 1, 0, 0])
    })
    initializeFrontOrBackCover(frontCanvas, true)
    initializeSpineBackground(spineCanvas, spineWidth)
    initializeFrontOrBackCover(backCanvas, false)
    activeCanvas.requestRenderAll()

    setShowDeleteModal(false)
  }

  const updateCover = async () => {
    const orderedImageUrls = await renderCanvases()
    const thumb = await generateThumbnail(orderedImageUrls)
    const fileName = await uploadThumbnailToBucket(
      thumb,
      bookDetail.id,
      coverName,
    )

    const payload = {
      bookId: bookDetail.id,
      spine: JSON.stringify(spineHistory.stack[spineHistory.stack.length - 1]),
      front: JSON.stringify(frontHistory.stack[frontHistory.stack.length - 1]),
      back: JSON.stringify(backHistory.stack[backHistory.stack.length - 1]),
      fileName,
    }

    const templateResult = await axiosClient.post(`/covers/${coverId}`, payload)

    if (templateResult.ok) {
      dispatchHistory({
        type: 'SAVE',
        key: fileName,
      })
      toast.success('Your cover is updated!')
    } else {
      toast.success('Something ... went wrong!')
    }
  }

  const saveCover = async (name) => {
    const orderedImageUrls = await renderCanvases()
    const thumb = await generateThumbnail(orderedImageUrls)
    const fileName = await uploadThumbnailToBucket(thumb, bookDetail.id, name)

    try {
      const payloadForPost = {
        name,
        spine: JSON.stringify(
          spineHistory.stack[spineHistory.stack.length - 1],
        ),
        front: JSON.stringify(
          frontHistory.stack[frontHistory.stack.length - 1],
        ),
        back: JSON.stringify(backHistory.stack[backHistory.stack.length - 1]),
        bookId: bookDetail?.id,
        fileName,
      }

      const res = await axiosClient.post(`/templates/create/`, payloadForPost)

      toast.success('Cover saved.')
      setShowSaveCoverModal(false)
      setCoverName(name)

      dispatchHistory({
        type: 'SAVE',
        key: fileName,
      })

      // TODO: Deal with this in the backend
      routerHistory.replace(
        `/dashboard/book/${bookDetail.id}/publish/cover-design/${res.data.templates.generatedFields[0].longValue}`,
      )
    } catch (error) {
      toast.error('Something went wrong!')

      throw error
    }
  }

  const previewCover = async () => {
    const orderedImages = await renderCanvases()
    const pdf = await createPdf(...orderedImages, spineWidth)
    window.open(pdf.output('bloburl'), '_blank')

    return pdf
  }

  const approveCover = async () => {
    const pdf = await previewCover()

    const file = new File([pdf.output('blob')], 'pdf', { type: 'pdf' })

    await uploadPdfToBucket(bookDetail.id, file)

    await updateBook(bookDetail.id, {
      cover_confirmed: true,
    })

    toast.success('Your cover is approved for printing!')
  }

  const classHiddenIfSpineInactive = {
    hidden: currentEditTarget !== 'spine',
  }
  const classHiddenIfSpineActive = {
    hidden: currentEditTarget === 'spine',
  }

  activeCanvas && activeCanvas.renderAll()

  if (!bookDetail.id) return <StyledLoader />

  return (
    <>
      <BookCoverContainer>
        <SaveCoverModal
          open={showSaveCoverModal}
          onSave={saveCover}
          onClose={() => setShowSaveCoverModal(false)}
          defaultCoverName={coverName}
        />

        <DeleteCoverModal
          show={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onDelete={deleteCover}
        />

        <div className="page-view-area">
          <div className="tools-section">
            <Accordion
              allowMultipleExpanded={true}
              allowZeroExpanded={true}
              className="add-element-tools"
            >
              <AccordionItem>
                <AccordionItemHeading>
                  <AccordionItemButton>{t('elements')}</AccordionItemButton>
                </AccordionItemHeading>
                <AccordionItemPanel>
                  <div>
                    <BiCircle
                      onClick={addCircle}
                      size={32}
                      className="mr-2 option-icon icon-style-2 cursor"
                      title={t('circle')}
                    />
                    <BiRectangle
                      onClick={addRectangle}
                      size={32}
                      className="mr-2 option-icon icon-style-2 cursor"
                      title={t('rectangle')}
                    />
                    <BiMinus
                      onClick={addLine}
                      size={32}
                      className="mr-2 option-icon icon-style-2 cursor"
                      title={t('line')}
                    />
                    <MdOutlineChangeHistory
                      onClick={addTriangle}
                      size={32}
                      className="mr-2 option-icon icon-style-2 cursor"
                      title={t('triangle')}
                    />
                  </div>
                </AccordionItemPanel>
              </AccordionItem>
              <AccordionItem>
                <AccordionItemHeading>
                  <AccordionItemButton>{t('text')}</AccordionItemButton>
                </AccordionItemHeading>
                <AccordionItemPanel>
                  <p
                    className={classNames('cursor', classHiddenIfSpineActive)}
                    onClick={addText}
                  >
                    <BiText
                      size={24}
                      className={classNames(
                        'mr-2 option-icon icon-style-1',
                        classHiddenIfSpineActive,
                      )}
                    />{' '}
                    {t('text')}
                  </p>

                  <p
                    className={classNames('cursor', classHiddenIfSpineInactive)}
                    onClick={addTextVerticallyTopToBottom}
                  >
                    <BiText
                      size={24}
                      className={classNames(
                        'mr-2 option-icon icon-style-1',
                        classHiddenIfSpineInactive,
                      )}
                    />{' '}
                    {t('top_to_bottom')}
                  </p>
                  <p
                    className={classNames('cursor', classHiddenIfSpineInactive)}
                    onClick={addTextVerticallyBottomToTop}
                  >
                    <BiText
                      size={24}
                      className={classNames(
                        'mr-2 mt-2 option-icon icon-style-1',
                        classHiddenIfSpineInactive,
                      )}
                    />{' '}
                    {t('bottom_to_top')}
                  </p>
                </AccordionItemPanel>
              </AccordionItem>
              <AccordionItem>
                <AccordionItemHeading>
                  <AccordionItemButton>{t('images')}</AccordionItemButton>
                </AccordionItemHeading>
                <AccordionItemPanel>
                  <div className="add-img-panel">
                    <div>
                      <form
                        onSubmit={(e) => addRemoteImgByUrl(e, remoteImgURL)}
                        className="mb-4"
                      >
                        <label>{t('load_image_by_url')} : &nbsp;</label>
                        <Input
                          type="text"
                          value={remoteImgURL}
                          placeholder={t('enter_image_url_here')}
                          onChange={(e) => setRemoteImgURL(e.target.value)}
                          className="mb-3 img-url-input"
                        />
                        <div className="d-flex">
                          <button
                            className="btn-blue btn-dash btn btn-primary"
                            type="submit"
                          >
                            {t('add_image')}
                          </button>
                          {addingRemoteImg && (
                            <div className="spinner-wrapper ml-2">
                              <SyncOutlined spin />
                            </div>
                          )}
                        </div>
                      </form>
                    </div>
                    <label>{t('select_image')} :</label>{' '}
                    <div className="d-flex upload-wrapper">
                      <input
                        className="img-upload"
                        type="file"
                        onChange={onUploadImage}
                      />
                      {addingLocalImg && (
                        <div className="spinner-wrapper ml-2">
                          <SyncOutlined spin />
                        </div>
                      )}
                    </div>
                  </div>
                </AccordionItemPanel>
              </AccordionItem>
            </Accordion>
            <div
              className="danger"
              style={{
                marginBottom: '15px',
                marginTop: '30px',
                padding: '4px 12px',
              }}
            >
              <p>
                <strong>{t('note')} : </strong> {t('note_text')}
              </p>
            </div>
          </div>

          <div
            id="mmain"
            className={classNames('canvas-area', {
              'flex-row': canvasSpacerConfig.isPillarBoxed,
            })}
            ref={canvasWrapperElRef}
          >
            {activeObject && (
              <div className="floating-tools ft-left">
                {isActiveTextObject && (
                  <>
                    <BiBold
                      title={t('bold')}
                      onClick={textBold}
                      size={24}
                      className="mt-2 cursor"
                    />
                    <BiItalic
                      title={t('italic')}
                      onClick={textItalic}
                      size={24}
                      className="mt-2 cursor"
                    />
                    <BiUnderline
                      title={t('underline')}
                      onClick={textUnderline}
                      size={24}
                      className="mt-2 cursor"
                    />
                    <AiOutlineLine
                      onClick={textStrikeThrough}
                      size={24}
                      className="mt-2 cursor"
                      title={t('overline')}
                    />
                    <BiAlignLeft
                      onClick={textLeft}
                      size={24}
                      className="mt-2 cursor"
                      title={t('left')}
                    />
                    <BiAlignMiddle
                      onClick={textCenter}
                      size={24}
                      className="mt-2 cursor"
                      title={t('center')}
                    />
                    <BiAlignRight
                      onClick={textRight}
                      size={24}
                      className="mt-2 cursor"
                      title={t('right')}
                    />
                  </>
                )}
                {isActiveImgObject && (
                  <>
                    <MdPhotoSizeSelectLarge
                      onClick={enterCropMode}
                      size={24}
                      className="mt-2 cursor"
                      title={t('select_image_to_crop')}
                    />
                  </>
                )}
                {cropImage && (
                  <BiCrop
                    onClick={applyCrop}
                    size={24}
                    className="mt-2 cursor"
                    title={t('crop')}
                  />
                )}
                {activeSelection.length ? (
                  <>
                    {activeObject?.angle !== 0 && (
                      <MdRotateLeft
                        onClick={resetRotation}
                        size={24}
                        className="mt-2 cursor"
                        title={t('reset_rotation')}
                      />
                    )}
                    <MdFlipToFront
                      onClick={sendForward}
                      size={24}
                      className="mt-2 cursor"
                      title={t('bring_forward')}
                    />
                    <MdFlipToBack
                      onClick={sendBackward}
                      size={24}
                      className="mt-2 cursor"
                      title={t('send_back')}
                    />
                    <BiTrash
                      onClick={deleteObj}
                      size={24}
                      className="mt-2 cursor"
                      title={t('delete_selected')}
                    />
                  </>
                ) : (
                  ''
                )}
              </div>
            )}
            <div
              className="canvas-buffer"
              style={{
                minWidth: canvasSpacerConfig.width,
                minHeight: canvasSpacerConfig.height,
              }}
            />
            <div
              className={classNames('canvas-wrapper', {
                hide: currentEditTarget !== 'front',
              })}
              onWheel={handleWheel}
            >
              <canvas ref={frontCanvasElRef} id="canvas-front" />
            </div>
            <div
              className={classNames('canvas-wrapper', {
                hide: currentEditTarget !== 'spine',
              })}
              onWheel={handleWheel}
            >
              <canvas ref={spineCanvasElRef} id="canvas-spine" />
            </div>
            <div
              className={classNames('canvas-wrapper', {
                hide: currentEditTarget !== 'back',
              })}
              onWheel={handleWheel}
            >
              <canvas ref={backCanvasElRef} id="canvas-back" />
            </div>
            <div
              className="canvas-buffer"
              style={{
                minWidth: canvasSpacerConfig.width,
                minHeight: canvasSpacerConfig.height,
              }}
            />
            <div className="floating-tools ft-right">
              <MdZoomIn
                size={24}
                title="Zoom in"
                className="cursor mb-1"
                onClick={() => zoomCanvas(activeCanvas, 1)}
              />
              <MdZoomOut
                size={24}
                title="Zoom out"
                className="cursor"
                onClick={() => zoomCanvas(activeCanvas, -1)}
              />
              <MdOpenWith
                size={24}
                title={isPanning ? 'Exit pan mode' : 'Enter pan mode'}
                className={classNames('cursor', { 'active-pan': isPanning })}
                onClick={togglePanMode}
              />
            </div>
          </div>

          <div className="tools-section">
            <Radio.Group
              defaultValue="front"
              buttonStyle="solid"
              onChange={(e) => {
                activeCanvas.discardActiveObject()
                activeCanvas.requestRenderAll()
                setCurrentEditTarget(e.target.value)
                setActiveCanvas(canvases[e.target.value])
                setActiveSelection([])
                setStrokeColorValue('black')
                setFillColorValue('#fff0')
              }}
              className="cover-section-tabs"
            >
              <Radio.Button className="cover-section-tab" value="front">
                {t('front_cover')}
              </Radio.Button>
              <Radio.Button className="cover-section-tab" value="spine">
                {t('spine')}
              </Radio.Button>
              <Radio.Button className="cover-section-tab" value="back">
                {t('back_cover')}
              </Radio.Button>
            </Radio.Group>
            <div className="file-controls">
              <div className="file-nav">
                <MdPreview
                  size={24}
                  className="mr-2 cursor"
                  title={t('preview')}
                  onClick={previewCover}
                />
                <MdSave
                  size={24}
                  className="mr-2 cursor"
                  title={t('save')}
                  onClick={() => {
                    if (coverId) {
                      updateCover()
                    } else {
                      setShowSaveCoverModal(true)
                    }
                  }}
                />
                {coverId && (
                  <MdSaveAs
                    size={24}
                    className="mr-2 cursor"
                    title={t('save_as')}
                    onClick={() => setShowSaveCoverModal(true)}
                  />
                )}
                <MdAssignmentTurnedIn
                  size={24}
                  className="mr-2 cursor approve-btn"
                  title="Approve for Print"
                  onClick={approveCover}
                />
                <MdDelete
                  size={24}
                  className="mr-2 cursor"
                  title="Delete"
                  onClick={() => setShowDeleteModal(true)}
                />
                <MdArrowBack
                  size={24}
                  className="mr-2 cursor"
                  title="Back to all covers"
                  onClick={() =>
                    routerHistory.push(
                      `/dashboard/book/${bookId}/publish/cover-templates`,
                    )
                  }
                />
              </div>
            </div>
            <div className="mt-2 d-flex justify-content-center undo-redo-btns">
              <BiUndo
                title={t('undo')}
                onClick={undo}
                size={24}
                className={classNames('mt-2', {
                  cursor: canUndo,
                  disabled: !canUndo,
                })}
              />
              <BiRedo
                title={t('redo')}
                onClick={redo}
                size={24}
                className={classNames('mt-2', {
                  cursor: canRedo,
                  disabled: !canRedo,
                })}
              />
            </div>
            <div className="background-controls mt-2">
              <label>
                Background Color:
                <ColorPicker
                  className="color-input"
                  type="color"
                  color={backgroundColor}
                  onClose={() => pushHistory(activeCanvas)}
                  onChange={(val) => {
                    if (currentEditTarget === 'front') {
                      setFrontBackgroundImage(null)
                      initializeFrontOrBackCover(activeCanvas, true)
                    } else if (currentEditTarget === 'spine') {
                      setSpineBackgroundImage(null)
                      initializeSpineBackground(spineCanvas, spineWidth)
                    } else if (currentEditTarget === 'back') {
                      setBackBackgroundImage(null)
                      initializeFrontOrBackCover(activeCanvas, false)
                    }
                    activeCanvas.backgroundColor = val
                    setBackgroundColor(val)
                    activeCanvas.requestRenderAll()
                  }}
                />
              </label>

              <BackgroundSelector
                background={currentCanvasBackgroundImg}
                onSelectBackground={async (pattern) => {
                  const img = await loadBackgroundImage(activeCanvas, pattern)

                  if (currentEditTarget === 'front') {
                    setFrontBackgroundImage(pattern)
                    initializeFrontOrBackCover(activeCanvas, true, img)
                  } else if (currentEditTarget === 'spine') {
                    setSpineBackgroundImage(pattern)
                    initializeSpineBackground(activeCanvas, spineWidth, img)
                  } else if (currentEditTarget === 'back') {
                    setBackBackgroundImage(pattern)
                    initializeFrontOrBackCover(activeCanvas, false, img)
                  }
                  setBackgroundColor('#ffffff')
                  activeCanvas.requestRenderAll()
                  dispatchHistory({
                    type: 'PUSH',
                    activeCanvas: currentEditTarget,
                    newState: activeCanvas.toJSON(),
                  })
                }}
                onRemoveBackground={() => {
                  if (currentEditTarget === 'front') {
                    setFrontBackgroundImage(null)
                    initializeFrontOrBackCover(activeCanvas, true, null)
                  } else if (currentEditTarget === 'spine') {
                    setSpineBackgroundImage(null)
                    initializeSpineBackground(activeCanvas, spineWidth)
                  } else if (currentEditTarget === 'back') {
                    setBackBackgroundImage(null)
                    initializeFrontOrBackCover(activeCanvas, false, null)
                  }
                  activeCanvas.requestRenderAll()
                  dispatchHistory({
                    type: 'PUSH',
                    activeCanvas: currentEditTarget,
                    newState: activeCanvas.toJSON(),
                  })
                }}
              />
            </div>
            <div className="font-controls">
              <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                {isActiveTextObject && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginRight: '20px',
                    }}
                  >
                    <label>{t('font_style')} &nbsp;</label>
                    <Form.Control
                      title={t('font_style')}
                      as="select"
                      value={fontFamilyValue}
                      onChange={textFontFamily}
                    >
                      <option value="" selected>
                        {t('font_style')}
                      </option>
                      <option
                        style={{ fontFamily: 'Myriad Pro Regular' }}
                        value="helvetica"
                        selected
                      >
                        Helvetica
                      </option>
                      {fonts.map((fontFamily) => (
                        <option
                          key={fontFamily}
                          style={{ fontFamily }}
                          value={fontFamily}
                        >
                          {fontFamily}
                        </option>
                      ))}
                    </Form.Control>
                  </div>
                )}
                {activeObject && !isActiveImgObject && (
                  <div className="d-flex flex-column">
                    <div className="color-tool">
                      <ColorPicker
                        className="strokeColor"
                        onChange={selectStrokeColor}
                        onClose={() =>
                          dispatchHistory({
                            type: 'PUSH',
                            activeCanvas: currentEditTarget,
                            newState: activeCanvas.toJSON(),
                          })
                        }
                        color={strokeColorValue}
                      />
                      <label>{t('stroke_color')} </label>
                    </div>

                    <div className="color-tool">
                      <ColorPicker
                        className="fillColor"
                        onChange={selectFillColor}
                        onClose={() =>
                          dispatchHistory({
                            type: 'PUSH',
                            activeCanvas: currentEditTarget,
                            newState: activeCanvas.toJSON(),
                          })
                        }
                        color={fillColorValue}
                      />
                      <label>
                        {t(isActiveTextObject ? 'font_color' : 'fill_color')}{' '}
                      </label>
                    </div>
                    <div>
                      <label>Stroke Width</label>
                      <input
                        type="number"
                        value={strokeWidth}
                        onChange={handleStrokeWidthChange}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </BookCoverContainer>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      <ToastContainer />
    </>
  )
}

export default withRouter(CoverEditor)
